import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchItems } from '../queries';
import {
  JourneyMap,
  JourneyMapAssignmentStatus,
  JourneyMapCompletionProvider,
  JourneyMapCompletionProviderRule,
  JourneyMapItemStatus,
  JourneyMapType,
} from '../../types/journeyMap';

export type JourneyMapsState = {
  maps: JourneyMap[];
  types: JourneyMapType[];
  assignmentStatuses: JourneyMapAssignmentStatus[];
  itemStatuses: JourneyMapItemStatus[];
  completionProviders: JourneyMapCompletionProvider[];
  completionProviderRules: JourneyMapCompletionProviderRule[];

  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  isReferenceDataLoading: boolean;
  isReferenceDataInitialized: boolean;
  referenceDataError: string | null;

  loading: boolean;
};

const initialState: JourneyMapsState = {
  maps: [],
  types: [],
  assignmentStatuses: [],
  itemStatuses: [],
  completionProviders: [],
  completionProviderRules: [],

  isLoading: false,
  isInitialized: false,
  error: null,

  isReferenceDataLoading: false,
  isReferenceDataInitialized: false,
  referenceDataError: null,

  loading: false,
};

export const fetchJourneyMaps = createAsyncThunk<
  JourneyMap[],
  void,
  { rejectValue: string }
>(
  'journeyMaps/fetchJourneyMaps',
  async (_, { rejectWithValue }) => {
    try {
      const maps = await fetchItems('db/journey-map/');
      return Array.isArray(maps) ? maps : [];
    } catch (err: any) {
      return rejectWithValue(err?.message || 'Failed to load journey maps');
    }
  },
);

export const fetchJourneyMapReferenceData = createAsyncThunk<
  {
    types: JourneyMapType[];
    assignmentStatuses: JourneyMapAssignmentStatus[];
    itemStatuses: JourneyMapItemStatus[];
    completionProviders: JourneyMapCompletionProvider[];
    completionProviderRules: JourneyMapCompletionProviderRule[];
  },
  void,
  { rejectValue: string }
>(
  'journeyMaps/fetchJourneyMapReferenceData',
  async (_, { rejectWithValue }) => {
    try {
      const [
        types,
        assignmentStatuses,
        itemStatuses,
        completionProviders,
        completionProviderRules,
      ] = await Promise.all([
        fetchItems('db/journey-map-type/').catch(() => []),
        fetchItems('db/journey-map-assignment-status/').catch(() => []),
        fetchItems('db/journey-map-item-status/').catch(() => []),
        fetchItems('db/journey-map-completion-provider/').catch(() => []),
        fetchItems('db/journey-map-completion-provider-rule/').catch(() => []),
      ]);

      return {
        types: Array.isArray(types) ? types : [],
        assignmentStatuses: Array.isArray(assignmentStatuses) ? assignmentStatuses : [],
        itemStatuses: Array.isArray(itemStatuses) ? itemStatuses : [],
        completionProviders: Array.isArray(completionProviders) ? completionProviders : [],
        completionProviderRules: Array.isArray(completionProviderRules)
          ? completionProviderRules
          : [],
      };
    } catch (err: any) {
      return rejectWithValue(err?.message || 'Failed to load journey map reference data');
    }
  },
);

export const checkJourneyMapsLoaded = createAsyncThunk<
  void,
  void,
  { state: { journeyMaps: JourneyMapsState } }
>(
  'journeyMaps/checkJourneyMapsLoaded',
  async (_, { dispatch, getState }) => {
    const state = getState();
    if (!state.journeyMaps.isInitialized && !state.journeyMaps.isLoading) {
      await dispatch(fetchJourneyMaps());
    }
  },
);

export const checkJourneyMapReferenceDataLoaded = createAsyncThunk<
  void,
  void,
  { state: { journeyMaps: JourneyMapsState } }
>(
  'journeyMaps/checkJourneyMapReferenceDataLoaded',
  async (_, { dispatch, getState }) => {
    const state = getState();
    if (
      !state.journeyMaps.isReferenceDataInitialized &&
      !state.journeyMaps.isReferenceDataLoading
    ) {
      await dispatch(fetchJourneyMapReferenceData());
    }
  },
);

const journeyMapsSlice = createSlice({
  name: 'journeyMaps',
  initialState,
  reducers: {
    setJourneyMaps(state, action: PayloadAction<JourneyMap[]>) {
      state.maps = action.payload || [];
      state.isInitialized = true;
    },

    upsertJourneyMap(state, action: PayloadAction<JourneyMap>) {
      const incoming = action.payload;
      const idx = state.maps.findIndex((m) => m.id === incoming.id);
      if (idx >= 0) state.maps[idx] = incoming;
      else state.maps.unshift(incoming);
    },

    setJourneyMapTypes(state, action: PayloadAction<JourneyMapType[]>) {
      state.types = action.payload || [];
      state.isReferenceDataInitialized = true;
    },

    addJourneyMapType(state, action: PayloadAction<JourneyMapType>) {
      const exists = state.types.some((t) => t.id === action.payload.id);
      if (!exists) state.types.push(action.payload);
      state.types.sort((a, b) => a.name.localeCompare(b.name));
    },

    setJourneyMapCompletionProviders(
      state,
      action: PayloadAction<JourneyMapCompletionProvider[]>,
    ) {
      state.completionProviders = action.payload || [];
    },

    upsertJourneyMapCompletionProvider(
      state,
      action: PayloadAction<JourneyMapCompletionProvider>,
    ) {
      const incoming = action.payload;
      const idx = state.completionProviders.findIndex((p) => p.id === incoming.id);
      if (idx >= 0) {
        state.completionProviders[idx] = incoming;
      } else {
        state.completionProviders.push(incoming);
      }
      state.completionProviders.sort((a, b) =>
        (a.display_name || '').localeCompare(b.display_name || ''),
      );
    },

    setJourneyMapCompletionProviderRules(
      state,
      action: PayloadAction<JourneyMapCompletionProviderRule[]>,
    ) {
      state.completionProviderRules = action.payload || [];
    },

    upsertJourneyMapCompletionProviderRule(
      state,
      action: PayloadAction<JourneyMapCompletionProviderRule>,
    ) {
      const incoming = action.payload;
      const idx = state.completionProviderRules.findIndex((r) => r.id === incoming.id);
      if (idx >= 0) {
        state.completionProviderRules[idx] = incoming;
      } else {
        state.completionProviderRules.push(incoming);
      }
    },

    setJourneyMapsLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
      state.isLoading = action.payload;
    },

    resetJourneyMapsState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJourneyMaps.pending, (state) => {
        state.isLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJourneyMaps.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        state.isInitialized = true;
        state.maps = action.payload || [];
      })
      .addCase(fetchJourneyMaps.rejected, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        state.error = action.payload || 'Failed to load journey maps';
      })

      .addCase(fetchJourneyMapReferenceData.pending, (state) => {
        state.isReferenceDataLoading = true;
        state.referenceDataError = null;
      })
      .addCase(fetchJourneyMapReferenceData.fulfilled, (state, action) => {
        state.isReferenceDataLoading = false;
        state.isReferenceDataInitialized = true;
        state.types = action.payload.types || [];
        state.assignmentStatuses = action.payload.assignmentStatuses || [];
        state.itemStatuses = action.payload.itemStatuses || [];
        state.completionProviders = action.payload.completionProviders || [];
        state.completionProviderRules = action.payload.completionProviderRules || [];
      })
      .addCase(fetchJourneyMapReferenceData.rejected, (state, action) => {
        state.isReferenceDataLoading = false;
        state.referenceDataError =
          action.payload || 'Failed to load journey map reference data';
      });
  },
});

export const {
  setJourneyMaps,
  upsertJourneyMap,
  setJourneyMapTypes,
  addJourneyMapType,
  setJourneyMapCompletionProviders,
  upsertJourneyMapCompletionProvider,
  setJourneyMapCompletionProviderRules,
  upsertJourneyMapCompletionProviderRule,
  setJourneyMapsLoading,
  resetJourneyMapsState,
} = journeyMapsSlice.actions;

export default journeyMapsSlice.reducer;